import { api } from '@/lib/api.js';
import { inFlightDedup } from '@/lib/inFlight.js';
import { getBrokerEmail } from '@/lib/auth.js';
import { HOLD_REFUSALS } from '@/services/paperwork.service.js';

// Single-deal reads and stage transitions. Reads are deduped; writes
// (stage transitions) bypass dedup and always hit the network.
//
// GET /api/tracon/deal/:idOrAppNumber returns { success, deal } where
// `deal` is the full pipeline item shape (id, deal_id, app_number,
// stage, urgency, quotes, aircraft, pilots, etc.). Reuses the 15min
// pipeline cache server-side so repeat workspace loads are cheap.

export function getDeal(dealId) {
  return inFlightDedup(`deal:${dealId}`, () =>
    api.get(`/api/tracon/deal/${encodeURIComponent(dealId)}`)
  );
}

// GET /api/tracon/deal/:dealId/hangar-submission — read-only resolve of the
// hangar packet link + carrier recipients (PAU / ABC by effective date / NHIP
// portal) for the V2 Submission panel. NOT a send: the broker mails from his
// own box. Returns { success, insurance_line, insured, packet_link,
// effective_date, portal_url, recipients:[{carrier,mode,name,email?,url?,ambiguous?}] }.
export function getHangarSubmission(dealId) {
  return inFlightDedup(`deal:hangar-submission:${dealId}`, () =>
    api.get(`/api/tracon/deal/${encodeURIComponent(dealId)}/hangar-submission`)
  );
}

// POST /api/tracon/regen-pdf/:dealId (broker-only). Regenerates the binding
// application PDF for a bound deal, re-uploads it to the client's WorkDrive
// folder, and re-stamps Signed_Application_Link. When usePersistedSignature is
// set (a bound deal WITH a session), the client signature captured at bind time
// is reproduced onto the app and the carrier TRIA disclosure page is re-appended
// (services/tria-append.service). OVERWRITES a client-visible document, so the
// UI gates this behind an explicit broker confirm. Emails no one. Returns
// { success, link, brand, signature_recovered, message, warnings? } (warnings:
// the names the application printed blank at "both", F3). Write, not deduped.
// A 409 FORM_CHANGED_SINCE_SIGNING (or SIGNED_WARRANTIES_MISSING) is an answer
// the dialog renders, not a failure the shell toasts (attestation S4-8): the
// application on file was signed over a pilot form that has since been
// attested again, cleared or removed, and the body names the pilots.
// NOT_AN_AVIATION_TERM (review fix 2026-11-01): a hangar or commercial term's application is its own packet, never regenerated
// here; could_not_check: the line could not be read. Both render in the dialog.
export const REGEN_REFUSALS = [{ status: 409, code: 'FORM_CHANGED_SINCE_SIGNING' }, { status: 409, code: 'SIGNED_WARRANTIES_MISSING' }, { status: 409, code: 'NOT_AN_AVIATION_TERM' }, { status: 503, code: 'could_not_check' }];
export function regenerateBindingApp(dealId, { usePersistedSignature = false } = {}) {
  return api.post(`/api/tracon/regen-pdf/${encodeURIComponent(dealId)}`, {
    use_persisted_signature: usePersistedSignature,
  }, { expected: REGEN_REFUSALS });
}

// POST /api/tracon/deal/:dealId/hangar-regenerate — broker-only. Rebuilds the
// hangar application packet from the Deal's session intake_data (reapplying any
// persisted quoted terms), re-uploads to WorkDrive, re-stamps
// Signed_Application_Link, and returns { success, link, deal_id }. This is also
// how a legacy hangar Deal gets a packet without a re-submit. Write — not deduped.
// reissueSigned: a BOUND deal's packet is the signed one; it re-issues from the
// stores and the mark captured at bind only on this explicit ask (the server
// 409s the plain call on a bound deal).
export function regenerateHangarPacket(dealId, { reissueSigned = false } = {}) {
  return api.post(`/api/tracon/deal/${encodeURIComponent(dealId)}/hangar-regenerate`, reissueSigned ? { reissue_signed: true } : {});
}

// POST /api/tracon/deal/:dealId/hangar-dispatch — broker-only, pre-bind.
// RETIRED for Phase 1 (server 410s unless HANGAR_TRACON_SEND=true — see
// HANGAR-DISPATCH-PHASE2-SPEC.md); the panel preps a Gmail send instead. Kept
// for Phase 2. Body { carriers: ['PAU'|'ABC'|'NHIP', ...], subject?, body? }.
// Response: { success, deal_id, dispatched:[{carrier,name,email,sent,error?}],
// portal:[{carrier,name,url}], any_sent }. Never auto-fires. Write — not deduped.
export function dispatchHangar(dealId, carriers, narrative = {}) {
  return api.post(`/api/tracon/deal/${encodeURIComponent(dealId)}/hangar-dispatch`, { carriers, ...narrative });
}

// PATCH /api/tracon/deal/:dealId/hangar-application — "Edit submission":
// broker corrections to the APPLICATION data (named insured, contact person,
// dates, per-location detail, requested liability tier, history, comments).
// Body is the FULL edited application; the server diffs it against the
// pristine intake_data and stores only the divergences at
// submission_workflow.application_overrides, then regenerates the packet.
// Returns { success, deal_id, application_overrides, link, regen_error, named_insured_sync, warnings }.
// Write — not deduped.
export function saveHangarApplication(dealId, application) {
  return api.patch(`/api/tracon/deal/${encodeURIComponent(dealId)}/hangar-application`, application);
}

// PATCH /api/tracon/deal/:dealId/hangar-terms — §9 broker quoted-terms entry.
// Body is the terms object ({ coverages, protection_class, liability_limit,
// updates }); the server sanitizes it to the generator's opts.terms shape and
// stores it at session.submission_workflow.hangar_terms. Returns
// { success, deal_id, hangar_terms }. Write — not deduped.
export function saveHangarTerms(dealId, terms) {
  return api.patch(`/api/tracon/deal/${encodeURIComponent(dealId)}/hangar-terms`, terms);
}

// POST /api/tracon/deal/:dealId/pilot/:contactId/exclude has no client any
// more (David, 2026-09-30): an exclusion is the carrier's answer on a quote,
// recorded as Excluded on that quote's Pilots step. The route stays for the
// API. The lift below remains so older deal-level entries can be cleared.
// DELETE the named exclusion. ?tail_number lifts only that aircraft's
// exclusion; omit it to lift every exclusion for the pilot. Does NOT re-rate
// the pilot — use the roster to put them back on a tail.
export function unexcludePilot(dealId, contactId, { tailNumber } = {}) {
  const qs = tailNumber ? `?tail_number=${encodeURIComponent(tailNumber)}` : '';
  return api.del(
    `/api/tracon/deal/${encodeURIComponent(dealId)}/pilot/${encodeURIComponent(contactId)}/exclude${qs}`,
  );
}

// POST /api/tracon/update-stage/:dealId
// DEAL-LIFECYCLE-SPEC Phase 1: lifecycle state is DERIVED — the only manual
// moves are the terminal states, and each requires a typed reason:
//   'Lost', 'Expired', 'Cancelled'
// Cancelled needs a signed LPR and the carrier's cancellation endorsement on file (round 2): a 409
// cancellation_documents_missing names what is missing in `error`, which the sheet shows.
export function updateDealStage(dealId, stage, extra = {}) {
  return api.post(`/api/tracon/update-stage/${encodeURIComponent(dealId)}`, {
    stage,
    ...extra,
  }, { expected: [{ status: 409, code: 'cancellation_documents_missing' }] });
}

// GET /api/tracon/deal/:dealId/cancellation-progress: { in_progress, missing, sentence }, derived on the server.
export function getCancellationProgress(dealId) {
  return api.get(`/api/tracon/deal/${encodeURIComponent(dealId)}/cancellation-progress`, {
    expected: [{ status: 503, code: 'could_not_check' }],
  });
}

// POST /api/tracon/deal/:dealId/effective-lock: the broker's lock on the start
// date. Answers { changed, lock } with the lock read back from the store.
export function setEffectiveLock(dealId, locked) {
  return api.post(`/api/tracon/deal/${encodeURIComponent(dealId)}/effective-lock`, { locked });
}

// POST /api/tracon/deal/:dealId/fees: the deal's fees. platformFee null =
// standard, 0 = waived, between = reduced (a reason with either); agencyFee null
// or 0 = none. Answers { fees } read back from the store.
export function setDealFees(dealId, { platformFee, reason, agencyFee }) {
  return api.post(`/api/tracon/deal/${encodeURIComponent(dealId)}/fees`, { platform_fee: platformFee, reason, agency_fee: agencyFee });
}

// GET /api/tracon/deals/:dealId/state — the derived lifecycle state with the
// facts behind it (the stage badge's "why" popover). (DEAL-LIFECYCLE Phase 1)
export function getDealState(dealId) {
  return api.get(`/api/tracon/deals/${encodeURIComponent(dealId)}/state`);
}

// ─── Paperwork drafts (DEAL-LIFECYCLE Phase 2) ────────────────────────────
export function listPaperDrafts(dealId, { status } = {}) {
  const qs = status ? `?status=${encodeURIComponent(status)}` : '';
  return api.get(`/api/tracon/deals/${encodeURIComponent(dealId)}/paper-drafts${qs}`);
}
export function createPaperDraft(dealId) {
  return api.post(`/api/tracon/deals/${encodeURIComponent(dealId)}/paper-drafts`, {});
}
export function approvePaperDraft(draftId) {
  return api.post(`/api/tracon/paper-drafts/${encodeURIComponent(draftId)}/approve`, {});
}
export function discardPaperDraft(draftId, reason) {
  return api.post(`/api/tracon/paper-drafts/${encodeURIComponent(draftId)}/discard`, { reason });
}
export function paperDraftPdfPath(draftId) {
  return `/api/tracon/paper-drafts/${encodeURIComponent(draftId)}/pdf`;
}

// POST /api/tracon/renew/:dealId — direct renewal fire off a Bound deal in
// the 0-100d RENEWAL_DUE window. Server runs the readiness predicate
// (utils/renewal-readiness.js); if any required field is missing it returns
// 400 { error: 'not_ready', missing: [...] }. PreStageRenewalSheet (intent
// 'initiate') calls this when the broker opens an already-complete deal and
// hits "Initiate renewal" WITHOUT editing — no pre-stage save needed. When
// the broker edits anything it calls preStageAndInitiateRenewal instead.
// broker_email is still sent, but the server ignores it: activity-log
// attribution comes from the verified token (verifiedBrokerEmail, 2026-10-14).
// Body: { effective_date: 'YYYY-MM-DD', auto_send_invite?: boolean, broker_email?: string }
// Response: { success, deal_id, deal_name, session_token, invite_sent }
export function initiateRenewal(dealId, { effectiveDate, autoSendInvite = true, brokerEmail } = {}) {
  return api.post(`/api/tracon/renew/${encodeURIComponent(dealId)}`, {
    effective_date: effectiveDate,
    auto_send_invite: autoSendInvite,
    broker_email: brokerEmail || getBrokerEmail() || undefined,
  });
}

// Chained save + initiate. PreStageRenewalSheet (intent 'initiate') calls
// this when the broker edited anything: the server saves the pre-stage
// payload, re-checks readiness on the merged in-memory state, then chains
// into the renewal flow atomically (single round-trip). Returns
// { success, renewal_readiness, renewal: { deal_id, deal_name, session_token, invite_sent } }
// or 400 { error: 'not_ready', missing: [...] } if the post-save predicate
// still fails (surfaced via the api.js caller-handled toast allowlist).
export function preStageAndInitiateRenewal(dealId, payload) {
  return api.post(
    `/api/tracon/command-center/deals/${encodeURIComponent(dealId)}/pre-stage`,
    { ...payload, initiate_after: true, broker_email: payload.broker_email || getBrokerEmail() || undefined },
  );
}

// POST /api/tracon/deals/:dealId/send-bundle — regenerate the Insurance
// Bundle (binder + statement) and email it to the client. Tower-bind path
// for deals with NO payment_request session; server 400s unless the deal
// Stage is Bound or Paid.
// Response: { success, fileName, permalink, certCount }
export function sendInsuranceBundle(dealId) {
  return api.post(`/api/tracon/deals/${encodeURIComponent(dealId)}/send-bundle`, {});
}

// GET /api/tracon/deals/:dealId/policy-docs — all filed docs for the deal,
// including client_visible state. Backs the "Publish documents" checklist
// (filtered client-side to the bundle + carrier policy_doc rows). (Gap 3)
export function getPolicyDocs(dealId) {
  return api.get(`/api/tracon/deals/${encodeURIComponent(dealId)}/policy-docs`);
}

// POST /api/tracon/deals/:dealId/publish-documents — flip client_visible=true
// on the chosen docs and notify the client (single-button email + SMS) with the
// OTP-gated portal link. Response: { success, published, portalUrl } (Gap 3)
export function publishDocuments(dealId, docIds) {
  return api.post(`/api/tracon/deals/${encodeURIComponent(dealId)}/publish-documents`, { docIds });
}

// POST /api/snkl/deal/:dealId/request — mint a SNKL signing token and email
// the Account primary contact. Server reads contact + carrier + policy off
// the Deal record. Optionally accept overrides for testing.
// Response: { success, token, link, expires_at, emailed_to }
export function requestSnkl(dealId, { contactEmail, contactName } = {}) {
  return api.post(`/api/snkl/deal/${encodeURIComponent(dealId)}/request`, {
    contactEmail,
    contactName,
  });
}

// POST /api/bor/deal/:dealId/request: mint a Tower signing link for the
// Broker of Record letter (filled from the deal's application) and email the
// Account primary contact. Mirrors requestSnkl. `message` is the broker's
// text to the client (the door validates it; omitted, the fallback paragraph);
// `choice` is the pick it started from (a preset key or 'custom').
export function requestBor(dealId, { contactEmail, contactName, message, choice } = {}) {
  return api.post(`/api/bor/deal/${encodeURIComponent(dealId)}/request`, {
    contactEmail,
    contactName,
    message,
    message_choice: choice,
  });
}

// GET /api/bor/deal/:dealId/request-messages: the message presets the BOR
// request offers, the Custom choice, the most characters a message may carry,
// and which preset to start on with the reason
// ({ presets, custom, max, preselect: { preset, why } };
// preset null when nothing could be checked).
export function getBorRequestMessages(dealId) {
  return api.get(`/api/bor/deal/${encodeURIComponent(dealId)}/request-messages`);
}

// POST /api/tracon/deal/:dealId/address-change/approve — broker approves a
// held out-of-state named-insured address change (Part B). Writes the parked
// address to the CRM Account and clears the hold, unblocking the sign step.
export function approveAddressChange(dealId) {
  return api.post(`/api/tracon/deal/${encodeURIComponent(dealId)}/address-change/approve`, {});
}

// POST .../address-change/deny — discard the held change (the prior address
// stands). A reason is required.
export function denyAddressChange(dealId, reason) {
  return api.post(`/api/tracon/deal/${encodeURIComponent(dealId)}/address-change/deny`, { reason });
}

// POST /api/builders-risk/deal/:dealId/remind - re-send the existing
// pending BR signing link without minting a new token. Returns 404 if
// there is no active token (broker should call requestBr instead).
// Writes BR_Last_Reminded_At on the Deal so the panel can show
// "last reminded Xh ago" and a future cron can rate-limit.
export function remindBr(dealId, { contactEmail, contactName } = {}) {
  return api.post(`/api/builders-risk/deal/${encodeURIComponent(dealId)}/remind`, {
    contactEmail,
    contactName,
  });
}

// POST /api/builders-risk/deal/:dealId/carrier-submitted - stamp
// BR_Carrier_Submitted_At / _To after the broker forwards the binding
// package to the carrier underwriter from Gmail. submittedTo is optional;
// blank lets the server resolve the carriers-table default contact.
// Response: { success, submitted_at, submitted_to, crm_write }
export function markBrCarrierSubmitted(dealId, { submittedTo } = {}) {
  return api.post(`/api/builders-risk/deal/${encodeURIComponent(dealId)}/carrier-submitted`, {
    submitted_to: submittedTo || '',
  });
}

// POST /api/builders-risk/deal/:dealId/request - mint a BR signing token
// and email the Account primary contact. Mirrors requestSnkl.
// includeSnkl=true bundles a SNKL letter into the same signing session
// (token.bundle_docs:['snkl']) so the insured signs once and both PDFs
// file. Default false; broker sets per-request.
// Response: { success, token, link, expires_at, emailed_to, includes_snkl }
export function requestBr(dealId, { contactEmail, contactName, includeSnkl } = {}) {
  return api.post(`/api/builders-risk/deal/${encodeURIComponent(dealId)}/request`, {
    contactEmail,
    contactName,
    include_snkl: includeSnkl === true,
  });
}

// GET /api/builders-risk/deal/:dealId/preview - broker-auth PDF blob of
// the BR application as it would render for the insured. No token mint,
// no DB writes, no emails. Used by BrRequestDialog to show the broker
// what is about to be sent. Mirrors previewLprPdf.
export async function previewBrPdf(dealId) {
  const token = localStorage.getItem('tracon_token');
  const res = await fetch(`/api/builders-risk/deal/${encodeURIComponent(dealId)}/preview`, {
    headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    if (res.status === 401 && body?.error) {
      window.dispatchEvent(new Event('auth:expired'));
    }
    throw new Error(body?.error || `preview ${res.status}`);
  }
  return res.blob();
}

// GET /api/builders-risk/deal/:dealId/preview-email - broker-auth.
// Returns { recipient, subject, html, placeholder_url, suggested_include_snkl }
// so the dialog can surface recipient confirmation, render the email body
// in an iframe, and seed the SNKL checkbox initial state from the
// Carrier's Default_Requires_SNKL Vendor field. The CTA link in the body
// uses placeholder_url; the real signing token is minted only when
// requestBr fires on Send.
export function previewBrEmail(dealId) {
  return api.get(`/api/builders-risk/deal/${encodeURIComponent(dealId)}/preview-email`);
}

// GET /api/builders-risk/deal/:dealId/data - broker-auth read for the
// BrDataSheet (BR-REPEAT-BIND Ship 2a). Response: { success, data (parsed
// Builders_Risk_Data blob), parse_error, readiness, enums, deal }.
export function getBrData(dealId) {
  return api.get(`/api/builders-risk/deal/${encodeURIComponent(dealId)}/data`);
}

// PUT /api/builders-risk/deal/:dealId/data - save the blob plus optional
// Deal fields (policy_number backfill, proposed_effective, expiration_date,
// amount). 400 with { errors: [...] } on validation failure; round-trips
// the stored blob + recomputed readiness on success.
export function saveBrData(dealId, { data, policyNumber, proposedEffective, expirationDate, amount } = {}) {
  return api.put(`/api/builders-risk/deal/${encodeURIComponent(dealId)}/data`, {
    data,
    policy_number: policyNumber,
    proposed_effective: proposedEffective,
    expiration_date: expirationDate,
    amount,
  });
}

// POST /api/builders-risk/deal/:dealId/clone - "Clone for next building"
// (Ship 2c). Fresh Deal for the same insured + carrier; Policy_Number,
// BR_*/SNKL_* state, payment fields, and the ND-APP autonumber all start
// blank server-side. Response: { success, deal_id, deal_name, application_number }.
export function cloneBrDeal(dealId, { dealName } = {}) {
  return api.post(`/api/builders-risk/deal/${encodeURIComponent(dealId)}/clone`, {
    deal_name: dealName,
  });
}

// POST /api/builders-risk/deal/:dealId/preview-token - broker
// preview-as-client (Ship 2d). Mints a preview-flagged signing token (no
// email, no BR_Status writes, 1 hour expiry); the Tower wizard renders
// read-only and bind-sign 403s the token server-side.
// Response: { success, preview: true, token, link, expires_at, includes_snkl }.
export function previewAsClientBr(dealId, { includeSnkl } = {}) {
  return api.post(`/api/builders-risk/deal/${encodeURIComponent(dealId)}/preview-token`, {
    include_snkl: includeSnkl === true,
  });
}

// GET /api/snkl/deal/:dealId/preview - broker-auth PDF blob of the SNKL
// letter rendered against current Deal state. Used by BrRequestDialog
// when the "Require Statement of No Known Losses" checkbox is on, to
// show the SNKL tab next to the BR application tab.
export async function previewSnklPdf(dealId) {
  const token = localStorage.getItem('tracon_token');
  const res = await fetch(`/api/snkl/deal/${encodeURIComponent(dealId)}/preview`, {
    headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    if (res.status === 401 && body?.error) {
      window.dispatchEvent(new Event('auth:expired'));
    }
    throw new Error(body?.error || `snkl preview ${res.status}`);
  }
  return res.blob();
}

// ── Commercial Aviation binding ──────────────────────────────────────────

export function requestCa(dealId, { contactEmail, contactName } = {}) {
  return api.post(`/api/commercial-aviation/deal/${encodeURIComponent(dealId)}/request`, {
    contactEmail,
    contactName,
  });
}

export async function previewCaPdf(dealId) {
  const token = localStorage.getItem('tracon_token');
  const res = await fetch(`/api/commercial-aviation/deal/${encodeURIComponent(dealId)}/preview`, {
    headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    if (res.status === 401 && body?.error) {
      window.dispatchEvent(new Event('auth:expired'));
    }
    throw new Error(body?.error || `ca preview ${res.status}`);
  }
  return res.blob();
}

export function previewCaEmail(dealId) {
  return api.get(`/api/commercial-aviation/deal/${encodeURIComponent(dealId)}/preview-email`);
}

// GET the deferred items that would ride this deal's renewal invoice (184 §4).
// Account-scoped and cross-deal: renewals open a NEW deal, so the items sit on
// the expiring one. Read-only; the broker sees them before anything sends.
export function getDeferredCollections(dealId, asOf) {
  const qs = asOf ? `?as_of=${encodeURIComponent(asOf)}` : '';
  return api.get(`/api/tracon/deals/${encodeURIComponent(dealId)}/deferred-collections${qs}`);
}

export function sendPaymentRequest(dealId, payload) {
  return api.post('/api/session/initiate', {
    crm_deal_id: dealId,
    mode: 'payment_request',
    ...payload,
  }, { expected: HOLD_REFUSALS }); // paperwork-1 L2-2: a hold is rendered by the sheet, never a generic toast
}

// The deal's open payment requests with their send history, and a
// broker-clicked reminder or resend (PAYMENT-REQUEST-MANAGER-SPEC §1-§3).
export function listPaymentRequests(dealId) {
  return api.get(`/api/tracon/deal/${encodeURIComponent(dealId)}/payment-requests`);
}

export function sendPaymentRequestAgain(sessionId, { kind = 'reminder', toEmail } = {}) {
  return api.post(`/api/tracon/payment-request/${encodeURIComponent(sessionId)}/send`, {
    kind,
    ...(toEmail ? { to_email: toEmail } : {}),
  });
}

export function cancelPaymentRequest(dealId) {
  return api.post(`/api/tracon/cancel-payment-request/${encodeURIComponent(dealId)}`);
}

// NICKEL-ACH-RETURN-CC-HANDOFF §5 — reverse a returned/declined Nickel payment.
// Walks the settled A/R back to failed and returns the deal to awaiting payment.
export function markPaymentReturned(dealId, { nickelRef, reason } = {}) {
  return api.post(`/api/tracon/mark-payment-returned/${encodeURIComponent(dealId)}`, {
    nickelRef: nickelRef || null,
    reason: reason || 'ACH returned',
  });
}

// includeVoided: the endpoint drops voided rows by default. The history
// panel asks for them (it strikes them through); MoneyStrip and
// PaymentRequestSheet want live rows only and keep the default.
export function getDealTransactions(dealId, { includeVoided = false } = {}) {
  const qs = includeVoided ? '?includeVoided=true' : '';
  return api.get(`/api/tracon/transactions/${encodeURIComponent(dealId)}${qs}`);
}

// Editable bind-snapshot prefill assembled from the live CRM Deal — backs the
// "Establish baseline" form for deals with no transactions yet.
// 200 §2: THE BIND SWITCH. Records that the carrier bound it, on the bind leg.
// It records; it does not send. `carrier_confirmation_ref` and `policy_number`
// are optional because the carrier often binds before issuing either.
export function confirmCarrierBind(txnId, body) {
  return api.post(`/api/tracon/transactions/${encodeURIComponent(txnId)}/carrier-confirm`, body);
}

export function getBaselinePrefill(dealId) {
  return api.get(`/api/tracon/transactions/${encodeURIComponent(dealId)}/baseline-prefill`);
}

// The producer of the deal's policy (deals-door-1 T16): a TRACON user, held in TRACON only.
// GET answers { ok, policy_id, crm_deal_id, producer: { user_id, label } | null, assignable? } (the
// assignable users only for the owner's own sign-in). A deal with no TRACON policy is 404 not_found and an
// unreadable store 503 could_not_check: both expected, the key says so in place.
export function getPolicyProducer(dealId) {
  return api.get(`/api/tracon/policies/${encodeURIComponent(dealId)}/producer`, {
    expected: [{ status: 404, code: 'not_found' }, { status: 503, code: 'could_not_check' }],
  });
}

// PATCH { user_id | null, reason }: the owner's own sign-in only (403 owner_only otherwise). Answers
// { ok, action, producer, warnings, fyi }; every refusal carries `message`, the broker's sentence, which the
// key toasts (so the known refusals are expected here, never toasted twice).
export function setPolicyProducer(dealId, { userId, reason }) {
  return api.patch(`/api/tracon/policies/${encodeURIComponent(dealId)}/producer`, { user_id: userId, reason }, {
    expected: [
      { status: 400, code: 'bad_request' }, { status: 400, code: 'reason_required' }, { status: 403, code: 'owner_only' },
      { status: 404, code: 'not_found' }, { status: 409, code: 'producer_not_assignable' }, { status: 409, code: 'policies_not_record' },
      { status: 503, code: 'policies_mode_unknown' },
    ],
  });
}

// The producer's share on this one policy (deals-door-1 answers round 3, R2c-Q1): PATCH { share: fraction 0 to 1 | null,
// reason }, the owner's own sign-in only. Null clears it (a reason required) and the producer's own rate applies again.
// Answers { ok, action, share }, share being the one rule's answer (utils/producer-share.js), rendered as given.
export function setPolicyProducerShare(dealId, { share, reason }) {
  return api.patch(`/api/tracon/policies/${encodeURIComponent(dealId)}/producer-share`, { share, reason }, {
    expected: [
      { status: 400, code: 'bad_request' }, { status: 400, code: 'reason_required' }, { status: 403, code: 'owner_only' },
      { status: 404, code: 'not_found' }, { status: 409, code: 'policies_not_record' }, { status: 503, code: 'policies_mode_unknown' },
    ],
  });
}

// Reopen an ended deal (deals-door-1 answers round, Q1, 2026-10-28): an edge-case tool, never the default path. GET answers
// { ok, word, ended, prior: { word } | null, may_reopen }; the control renders only when the server says may_reopen and a prior
// word is known (the page derives nothing). POST { reason } answers { ok, from, to, reason }; every refusal carries `message`.
export function getDealReopen(dealId) {
  return api.get(`/api/tracon/deal/${encodeURIComponent(dealId)}/reopen`, {
    expected: [{ status: 404, code: 'not_found' }, { status: 503, code: 'could_not_check' }],
  });
}
// stage (QA-1): the owner's chosen word, sent only when the server offered the choice (may_choose).
export function reopenDeal(dealId, reason, stage) {
  return api.post(`/api/tracon/deal/${encodeURIComponent(dealId)}/reopen`, { reason, ...(stage ? { stage } : {}) }, {
    expected: [
      { status: 400, code: 'reason_required' }, { status: 403, code: 'privilege_missing' }, { status: 403, code: 'personal_privilege_only' },
      { status: 404, code: 'not_found' }, { status: 409, code: 'not_ended' }, { status: 409, code: 'reopen_prior_unknown' },
      { status: 409, code: 'no_deal_id' }, { status: 409, code: 'reinstatement_endorsement_missing' }, { status: 503, code: 'could_not_check' },
      { status: 403, code: 'owner_only' }, { status: 400, code: 'stage_not_allowed' }, { status: 400, code: 'prior_known' },
    ],
  });
}

// The endorsement window for this deal's term (deals-door-1 T15): the server's defaults, min/max and hint sentence.
// The sheet renders them and derives nothing. A failed read rejects; the sheet says so and invents no bound.
export function getEndorsementWindow(dealId) {
  return api.get(`/api/tracon/transactions/${encodeURIComponent(dealId)}/endorsement-window`);
}

// Author a policy transaction (bind / endorsement / cancellation / correction).
// created_by is stamped from the broker JWT. Returns { success, transaction }.
export function createTransaction(dealId, payload) {
  return api.post(`/api/tracon/transactions/${encodeURIComponent(dealId)}/create`, {
    created_by: getBrokerEmail() || 'broker',
    ...payload,
  });
}

// 190 §4: the financed placement handle. One door on the server writes both
// legs; the caller reads the ledger back through getDealTransactions rather
// than trusting this response.
export function recordFinancedPlacement(txId, { financed_by, ref, funded_on }) {
  return api.post(`/api/tracon/transactions/${encodeURIComponent(txId)}/financed`, { financed_by, ref, funded_on });
}

// The live trust account names, for the answers that land cash. A failed read
// rejects; the caller shows an empty select rather than inventing an account.
export function getTrustAccountNames() {
  return api.get('/api/money/accounts').then((r) => (r?.accounts || []).filter((a) => a.active !== false).map((a) => a.name));
}

// 190 ADDENDUM §D: record how a bind was actually paid. Six answers, one
// server mapper (utils/ar-answer.js), each through the door that owns it.
export function recordHowPaid(txId, answer) {
  return api.post(`/api/tracon/transactions/${encodeURIComponent(txId)}/how-paid`, answer);
}

// A pending Nickel ACH that cleared (the day is the one on Nickel's "Processed" email).
export function recordNickelAchCleared(txId, clearedOn) {
  return api.post(`/api/tracon/transactions/${encodeURIComponent(txId)}/nickel-cleared`, { cleared_on: clearedOn });
}

// Signed paperwork received on a live bind (Q4, 2026-10-28): the day it arrived, stated by the broker. Answers
// { success, transaction, closed_on, waiting_for }; a refusal carries code (ALREADY_SIGNED, NOT_A_LIVE_BIND, ...).
// Signed LPR received on a live cancellation (deals-door-1 round 3, R2k-Q7): the day it arrived, stated by the broker.
// Answers { success, transaction, cancellation: { state, sentence, missing } }; a personal login only (403 otherwise).
export function recordSignedLprReceived(txId, receivedOn) {
  return api.post(`/api/tracon/transactions/${encodeURIComponent(txId)}/signed-lpr-received`, { received_on: receivedOn });
}
export function recordSignedPaperworkReceived(txId, receivedOn) {
  return api.post(`/api/tracon/transactions/${encodeURIComponent(txId)}/signed-paperwork-received`, { received_on: receivedOn }, { expected: HOLD_REFUSALS });
}

// Deferred back to unpaid, so a payment request can collect it (Mark Green, 2026-10-06).
export function endDeferral(txId) {
  return api.post(`/api/tracon/transactions/${encodeURIComponent(txId)}/end-deferral`, {});
}

export function voidTransaction(txId, voidedReason) {
  return api.post(`/api/tracon/transactions/${encodeURIComponent(txId)}/void`, {
    voided_reason: voidedReason,
    voided_by: getBrokerEmail() || 'broker',
  });
}

// Blank PHF (aircraft-scoped, no pilot identity). email present → mint + send;
// email absent → mint only, caller copies the returned link.
// Returns { success, link, sent, to }.
export function sendBlankPHF(dealId, payload) {
  return api.post(`/api/tracon/deal/${encodeURIComponent(dealId)}/blank-phf/send`, payload);
}

// Client add-pilot link: one reusable link per policy (ndemnifly.com/phf?t=).
// get returns the live link, minting it on first ask; reset revokes it and
// mints the replacement. Returns { success, link, minted, created_at }.
export function getAddPilotLink(dealId) {
  return api.post(`/api/tracon/deal/${encodeURIComponent(dealId)}/add-pilot-link`, {});
}

export function resetAddPilotLink(dealId) {
  return api.post(`/api/tracon/deal/${encodeURIComponent(dealId)}/add-pilot-link/reset`, {});
}

// Standalone blank-PHF (no deal required). resolve = FAA prefill + deal
// auto-match by tail; send mirrors sendBlankPHF with optional deal_id.
export function resolveBlankPHF(nNumber) {
  return api.get(`/api/tracon/blank-phf/resolve/${encodeURIComponent(nNumber)}`);
}

export function sendBlankPHFStandalone(payload) {
  return api.post('/api/tracon/blank-phf/send', payload);
}

// ── Rewrite door (REWRITE-AND-CREDITS-SPEC Part A) ──────────────────────────

// Search existing CRM Accounts for the "pick existing insured" control.
export function searchAccounts(q) {
  const query = (q || '').trim();
  if (query.length < 2) return Promise.resolve({ success: true, results: [] });
  return inFlightDedup(`acct-search:${query}`, () =>
    api.get(`/api/tracon/accounts/search?q=${encodeURIComponent(query)}`)
  );
}

// New deal from this policy (LAPSED-CLIENT-REUP-SPEC §2.2). body:
// { insured: 'same'|'existing'|'new', target_account?, contact_id?, effective_date,
//   name_primary_as_pilot, initiate_session }. Returns the new deal plus
// session_token / invite_status / warnings from the chain.
export function rewriteDeal(dealId, body) {
  return api.post(`/api/tracon/deal/${encodeURIComponent(dealId)}/rewrite`, {
    broker_email: getBrokerEmail() || undefined,
    ...body,
  });
}

// What the sheet shows before the click (§3.0, §3.1): the source facts, the
// roster, and the open follow-ups that creating the deal will close. One
// server-normalized payload, whichever surface opened the door.
export function getRewriteContext(dealId) {
  return api.get(`/api/tracon/deal/${encodeURIComponent(dealId)}/rewrite-context?_t=${Date.now()}`);
}

// ── Credits (REWRITE-AND-CREDITS-SPEC Part B) ───────────────────────────────

// Available credits for the "Apply credit" picker (scoped to the broker).
export function getAvailableCredits() {
  return api.get('/api/tracon/credits/available');
}

// Credits the payment request may apply on THIS deal, each carrying its
// trigger (`via`: same_account | rewrite). The server decides; this only
// mirrors it (David 2026-09-30).
export function getApplicableCredits(dealId) {
  return api.get(`/api/tracon/deal/${encodeURIComponent(dealId)}/credits/applicable`);
}

// Credits touching a deal (both sides, fronted-annotated).
export function getDealCredits(dealId) {
  return api.get(`/api/tracon/deal/${encodeURIComponent(dealId)}/credits`);
}

// Credits sourced from an account (money zone, fronted-annotated).
export function getAccountCredits(accountId) {
  return api.get(`/api/tracon/account/${encodeURIComponent(accountId)}/credits`);
}

// Manual "Add credit". body: { source_crm_deal_id, source_account_id, amount, memo }.
export function createCredit(body) {
  return api.post('/api/tracon/credits', body);
}

// Broker void — reason required.
export function voidCredit(creditId, reason) {
  return api.post(`/api/tracon/credits/${encodeURIComponent(creditId)}/void`, { reason });
}

// Broker refund — money paid back as cash. Reason required; amount optional
// (omit for the whole credit, pass a smaller one for a partial). Refunded-ness
// is an amount and a date, never a status word, because a credit can be $164
// with $50 refunded and no single word can say "partly".
//
// NOT a reversal (the money never arrived) and NOT a void (we made a mistake).
// This records that David paid money back; it moves nothing itself.
export function refundCredit(creditId, { amount, reason, refunded_on } = {}) {
  return api.post(`/api/tracon/credits/${encodeURIComponent(creditId)}/refund`, {
    ...(amount != null && amount !== '' ? { amount: Number(amount) } : {}),
    reason,
    ...(refunded_on ? { refunded_on } : {}),
  });
}

// ─── Fleet coverage (AIRCRAFT-CANONICALIZATION-PHASE1-SPEC 1c) ─────────────
//
// The deal's CANONICAL per-term aircraft rows, read straight from Supabase
// policy_aircraft rather than the CRM-derived pipeline fleet. That's the whole
// point: the panel shows what the carrier will actually be sent, so a missing
// physical-damage basis is visible instead of surfacing later as "no terms."

// GET → { success, session_id, editable, bases[], aircraft[] }
export function getFleetCoverage(dealId) {
  return inFlightDedup(`deal:fleet-coverage:${dealId}`, () =>
    api.get(`/api/tracon/deal/${encodeURIComponent(dealId)}/fleet-coverage`)
  );
}

// PATCH one aircraft's per-term coverage through the single writer. Sparse:
// send only the keys being changed. Write — not deduped.
// patch: { hull_value, coverage_basis, deductible_in_motion,
//          deductible_not_in_motion, base_airport, aircraft_use }
// Returns { success, aircraft, updated[], warnings[] } — `warnings` carries
// CRM-mirror failures, which do NOT fail the canonical write.
// THE RUNWAY AT A BASE AIRPORT (services/airport-runway.service.js): the one
// resolver's answer with its source, and the broker's stated runway when the
// resolver has none.
export function getAirportRunway(ident) {
  return api.get(`/api/tracon/airports/${encodeURIComponent(ident)}/runway`);
}
export function stateAirportRunway(ident, body) {
  return api.put(`/api/tracon/airports/${encodeURIComponent(ident)}/runway`, body);
}

export function updateAircraftCoverage(dealId, registration, patch) {
  return api.patch(
    `/api/tracon/deal/${encodeURIComponent(dealId)}/aircraft/${encodeURIComponent(registration)}/coverage`,
    patch
  );
}

// THE CLIENT'S PICK. GET is the copy the binding page reads (the live bind
// transaction) beside the quote it came from, with what changed since. POST
// send-back voids it and puts the quotes back in front of the client; the
// server refuses when paid, signed, broker-recorded or ambiguous. pick_id is
// the pick the broker was shown, so a pick replaced meanwhile is not touched.
export function getClientPick(dealId) {
  return api.get(`/api/session/admin/client-pick/${encodeURIComponent(dealId)}`);
}
export function sendBackClientPick(dealId, pickId) {
  return api.post(`/api/session/admin/send-back-pick/${encodeURIComponent(dealId)}`, { pick_id: pickId });
}

// THE EXPERIENCE SELECTION DOOR (2026-10-29): routes/experience-model.routes.js
export function listFaaMakes() {
  return api.get('/api/tracon/experience-models/faa-makes');
}
export function listFaaModelsOfMake(make) {
  return api.get(`/api/tracon/experience-models/faa-models?make=${encodeURIComponent(make)}`);
}
export function stateExperienceHours(body) {
  return api.post('/api/tracon/experience-models/hours', body);
}
export function stateExperienceRowModel(body) {
  return api.post('/api/tracon/experience-models', body);
}

// The deal's policy number through THE policy writer (PATCH /deal/:id/policy-info
// → services/policy-info.service updatePolicyInfo; in both mode it writes the
// policies row first, then the Deal). The header key and the policy-docs drop
// both call this; neither writes the number any other way.
export async function setPolicyNumber(dealId, policyNumber) {
  return api.patch(`/api/tracon/deal/${encodeURIComponent(dealId)}/policy-info`, { policy_number: String(policyNumber || '').trim() });
}

// THE DEAL'S DOCUMENT DOOR (paperwork-1 D7, A18; routes/documents.routes.js): every document dropped on a deal is filed in the
// one home through its one writer. fileDealDocuments posts several files in one request, each with its own item
// ({ kind, transaction_id?, interest_id?, document_date?, document_date_kind?, signed_on? (an application's signing day,
// required by the door: David Q6), replaces_document_id? }), and answers the
// server's body as it stands, refusals included ({ success, results: [{ ok, status, code, error, document, candidates }] }):
// the caller renders each file's answer. A quote is never posted here (the quote drop is uploadQuoteFile).
export async function fileDealDocuments(dealId, entries, { notes = '' } = {}) {
  const formData = new FormData();
  const items = [];
  for (const e of entries) {
    formData.append('files', e.file, e.file.name);
    const item = { kind: e.kind };
    for (const k of ['transaction_id', 'interest_id', 'document_date', 'document_date_kind', 'signed_on', 'replaces_document_id']) if (e[k] !== undefined && e[k] !== null && e[k] !== '') item[k] = e[k];
    items.push(item);
  }
  formData.append('items', JSON.stringify(items));
  if (String(notes || '').trim()) formData.append('notes', String(notes).trim());
  const res = await fetch(`/api/tracon/deals/${encodeURIComponent(dealId)}/documents`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${localStorage.getItem('tracon_token')}` },
    body: formData,
  });
  const body = await res.json().catch(() => ({}));
  if (!Array.isArray(body.results)) throw new Error(body.error || `The documents door answered ${res.status}`);
  return body;
}

// BEFORE filing (A18): the derived name, whether the bytes are NDEMNIFLY's own paper (no carrier kind is then offered), what
// is already filed with these bytes, and what this would replace. The refusals the drop dialog renders are expected.
// The preview's own refusals (2026-11-02, one refusal one toast): the drop's hook says each one once, naming the file, so api.js
// must not toast them too (hooks/useDocumentDrop.js DROP_REFUSAL_STATUSES).
const DROP_EXPECTED = [{ status: 409, code: 'NO_POLICY_ROW' }, { status: 400, code: 'BAD_REQUEST' }, { status: 503, code: 'store_unavailable' }];
export function previewDocumentName(dealId, params) {
  const q = new URLSearchParams(Object.entries(params || {}).filter(([, v]) => v !== undefined && v !== null && v !== ''));
  return api.get(`/api/tracon/deals/${encodeURIComponent(dealId)}/documents/preview-name?${q.toString()}`, { expected: DROP_EXPECTED });
}
// The ledger rows each carrier kind may be attached to (the server's rule), and the policy the drop files on.
export function getDropContext(dealId) {
  return api.get(`/api/tracon/deals/${encodeURIComponent(dealId)}/documents/drop-context`, { expected: DROP_EXPECTED });
}
// "Wrong type? Change it": the filed row re-typed through placeDocument; answers { document } read back with its name.
export function placeDealDocument(documentId, body) {
  return api.post(`/api/tracon/documents/${encodeURIComponent(documentId)}/place`, body);
}
// "Rename" (N14): a stated name for the document, or null to use its derived name again; answers { document } read back with
// both names (name, derived_name, name_overridden).
// A name another document of the policy goes by (409 NAME_TAKEN, review of 2026-11-02) or a rename that lost a race (409
// DOC_CHANGED) is expected: the Paperwork tab says it once, on the row and in one toast.
const RENAME_EXPECTED = [{ status: 409, code: 'NAME_TAKEN' }, { status: 409, code: 'DOC_CHANGED' }];
export function renameDealDocument(documentId, name) {
  return api.post(`/api/tracon/documents/${encodeURIComponent(documentId)}/rename`, { name }, { expected: RENAME_EXPECTED });
}
// "Wrong policy? Move it" (review fix M4): the server's list of policies it may move to, and the move (placeDocument on that
// policy; the confirmation is cleared, so it is confirmed again there).
export function getDocumentMoveChoices(documentId) {
  return api.get(`/api/tracon/documents/${encodeURIComponent(documentId)}/move-choices`);
}
export function moveDealDocument(documentId, policyId) {
  return api.post(`/api/tracon/documents/${encodeURIComponent(documentId)}/move`, { policy_id: policyId });
}

// The ledger changed on this deal (a carrier document linked, a return premium entered): the Transaction history panel and
// the cancellation note both re-read their own endpoint.
export const LEDGER_CHANGED_EVENT = 'tracon:ledger-changed';
export function announceLedgerChanged(dealId) {
  if (typeof window !== 'undefined') window.dispatchEvent(new CustomEvent(LEDGER_CHANGED_EVENT, { detail: { dealId: String(dealId) } }));
}
// A document was filed or re-typed on this deal (the page-wide drop, the policy documents sheet): the surfaces that list the
// deal's documents (the Docs tab's filed list, the interests panel's certificate status) re-read their own endpoint, so the
// page never shows the state from before the drop (L1b-B4, Chrome pass).
export const DOCUMENTS_CHANGED_EVENT = 'tracon:documents-changed';
export function announceDocumentsChanged(dealId) {
  if (typeof window !== 'undefined') window.dispatchEvent(new CustomEvent(DOCUMENTS_CHANGED_EVENT, { detail: { dealId: String(dealId) } }));
}
export function stateRegistryMatch(body) {
  return api.post('/api/tracon/experience-models/registry-match', body);
}

// THE HANGAR ANNUAL REVIEW (main batch 2026-11-01, N1; routes/hangar-review-broker.routes.js). GET is the card: { applies,
// state, words, review, prior }. Start and withdraw answer the card read back (start adds the client's link). Writes bypass dedup.
export function getHangarReview(dealId) {
  return inFlightDedup(`deal:hangar-review:${dealId}`, () =>
    api.get(`/api/tracon/deal/${encodeURIComponent(dealId)}/hangar-review`)
  );
}
// The refusals the card renders in its own words (the server's sentence), so the generic toast stays quiet for them.
const HANGAR_REVIEW_REFUSALS = ['REVIEW_LIVE', 'THIS_TERM_ON_FILE', 'THIS_TERM_EDITED', 'NOT_A_HANGAR_APPLICATION', 'NO_PRIOR_APPLICATION', 'NOT_A_RENEWAL', 'NO_POLICY_ROW', 'PRIOR_HAS_NO_DEAL', 'REVIEW_NOT_LIVE', 'REASON_REQUIRED', 'REASON_TOO_LONG']
  .flatMap((code) => [{ status: 409, code }, { status: 400, code }]);
export function startHangarReview(dealId) {
  return api.post(`/api/tracon/deal/${encodeURIComponent(dealId)}/hangar-review/start`, {}, { expected: HANGAR_REVIEW_REFUSALS });
}
export function withdrawHangarReview(dealId, reviewId, reason) {
  return api.post(`/api/tracon/deal/${encodeURIComponent(dealId)}/hangar-review/withdraw`, { review_id: reviewId, reason }, { expected: HANGAR_REVIEW_REFUSALS });
}
